import { ButtonVariation, MerchantSettings } from '@client/typings/Common';
import type { ButtonConfig } from '@components/buttons/types';
import type { IAppContext } from '@client/contexts/AppContext';
import {
  SecondButtonType,
  ButtonShape,
  ButtonSize,
  ButtonColorType,
  ButtonLayout
} from '@components/Settings/types';
import { CheckoutPaymentOptionsOtherThanPayPal } from '@client/components/buttons/types';
import {
  DEFAULT_MERCHANT_SETTINGS,
  DEFAULT_MERCHANT_CART_BUTTON_SETTINGS,
  DEFAULT_MERCHANT_CHECKOUT_SETTINGS
} from './getInitialMerchantSettings';

// some fields from setting but not defined in Buttonconfig.
export type ButtonConfigExtensionFromSettings = {
  paymentMethodVenmo?: boolean;
  paymentMethodPayLater?: boolean;
  paymentMethodApplePay?: boolean;
  showBusinessName?: boolean;
  showBusinessLogo?: boolean;
  homepageUrl?: string;
  secondButtonType?: SecondButtonType;
  singelButtonShape?: ButtonShape;
  cartButtonShape?: ButtonShape;
  cartButtonSize?: ButtonSize;
  cartButtonColorType?: ButtonColorType;
  cartButtonColorCode?: string;
};

type ButtonVariablesFromSettings = {
  payment_method_paylater?: boolean;
  payment_method_venmo?: boolean;
  payment_method_applepay?: boolean;
  show_business_name?: boolean;
  show_business_logo?: boolean;
  homepage_url?: string;
  layout?: ButtonLayout;
  shape?: ButtonShape;
  size?: ButtonSize;
  color?: ButtonColorType;
  second_button_type?: SecondButtonType;
  checkout_button_text_type?: string;
  checkout_button_custom_text?: string;
  single_button_shape?: ButtonShape;
  single_button_color_type?: ButtonColorType;
  single_button_color?: string;
  single_button_size?: ButtonSize;
  cart_button_shape?: ButtonShape;
  cart_button_size?: ButtonSize;
  cart_button_color_type?: ButtonColorType;
  cart_button_color_code?: string;
};

type SettingsFieldsMapper = {
  configMapper: keyof ButtonConfig;
  variablesMapper: keyof ButtonVariablesFromSettings;
  variableHandler?: (data) => string | undefined;
};

const BooleanButtonVariableHandler = (data: boolean) => `${data}`;

const SETTINGS_FIELDS_CHECKOUT: { [K in keyof MerchantSettings]?: SettingsFieldsMapper } = {
  payment_method_paylater: {
    configMapper: 'paymentMethodPayLater',
    variablesMapper: 'payment_method_paylater',
    variableHandler: BooleanButtonVariableHandler
  },
  payment_method_venmo: {
    configMapper: 'paymentMethodVenmo',
    variablesMapper: 'payment_method_venmo',
    variableHandler: BooleanButtonVariableHandler
  },
  payment_method_apple_pay: {
    configMapper: 'paymentMethodApplePay',
    variablesMapper: 'payment_method_applepay',
    variableHandler: BooleanButtonVariableHandler
  },
  // TODO: Support Google Pay once Google Pay integration provided
  // payment_method_google_pay: {
  //   configMapper: 'paymentMethodGooglePay',
  //   variablesMapper: 'payment_method_googlepay',
  //   variableHandler: BooleanButtonVariableHandler
  // },
  // TODO: Support paypal button message once JSSDK inner source work completed
  // paypal_button_message: {
  //   configMapper: 'paypaButtonMessage',
  //   variablesMapper: 'paypal_button_message',
  //   variableHandler: BooleanButtonVariableHandler
  // },
  show_business_name: {
    configMapper: 'showBusinessName',
    variablesMapper: 'show_business_name',
    variableHandler: BooleanButtonVariableHandler
  },
  show_business_logo: {
    configMapper: 'showBusinessLogo',
    variablesMapper: 'show_business_logo',
    variableHandler: BooleanButtonVariableHandler
  },
  homepage_url: {
    configMapper: 'homepageUrl',
    variablesMapper: 'homepage_url'
  }
};

const SETTINGS_FIELDS_STACKED_BUTTON: {
  [K in keyof MerchantSettings]?: SettingsFieldsMapper;
} = {
  stacked_button_layout: {
    configMapper: 'layout',
    variablesMapper: 'layout'
  },
  stacked_button_shape: {
    configMapper: 'shape',
    variablesMapper: 'shape'
  },
  stacked_button_size: {
    configMapper: 'size',
    variablesMapper: 'size'
  },
  stacked_button_color_type: {
    configMapper: 'color',
    variablesMapper: 'color'
  },
  stacked_button_second_button_type: {
    configMapper: 'secondButtonType',
    variablesMapper: 'second_button_type'
  },
  stacked_button_text_type: {
    configMapper: 'checkoutButtonTextType',
    variablesMapper: 'checkout_button_text_type'
  },
  stacked_button_custom_text: {
    configMapper: 'checkoutButtonCustomText',
    variablesMapper: 'checkout_button_custom_text'
  }
};

const SETTINGS_FIELDS_SINGLE_BUTTON: {
  [K in keyof MerchantSettings]?: SettingsFieldsMapper;
} = {
  single_button_shape: {
    configMapper: 'singelButtonShape',
    variablesMapper: 'single_button_shape'
  },
  single_button_size: {
    configMapper: 'singleButtonSize',
    variablesMapper: 'single_button_size'
  },
  single_button_color_type: {
    configMapper: 'singleButtonColorType',
    variablesMapper: 'single_button_color_type'
  },
  single_button_color_code: {
    configMapper: 'singleButtonColor',
    variablesMapper: 'single_button_color'
  }
};

const SETTINGS_FIELDS_CART_BUTTON: {
  [K in keyof MerchantSettings]?: SettingsFieldsMapper;
} = {
  cart_button_shape: {
    configMapper: 'cartButtonShape',
    variablesMapper: 'cart_button_shape'
  },
  cart_button_size: {
    configMapper: 'cartButtonSize',
    variablesMapper: 'cart_button_size'
  },
  cart_button_color_type: {
    configMapper: 'cartButtonColorType',
    variablesMapper: 'cart_button_color_type'
  },
  cart_button_color_code: {
    configMapper: 'cartButtonColorCode',
    variablesMapper: 'cart_button_color_code'
  }
};

// TODO: Need to add the fields for subscriptions settings in the future stories DTPRP-2458
const SETTINGS_FIELDS_SUBSCRIPTIONS = {};

const DEFAULT_FIELDS_MAPPER = {
  ...SETTINGS_FIELDS_CHECKOUT,
  ...SETTINGS_FIELDS_STACKED_BUTTON,
  ...SETTINGS_FIELDS_SINGLE_BUTTON
};

const DEFAULT_CART_FIELDS_MAPPER = {
  ...SETTINGS_FIELDS_CART_BUTTON,
  ...SETTINGS_FIELDS_CHECKOUT
};

const DEFAULT_CART_BUTTON_SETTINGS = {
  ...DEFAULT_MERCHANT_CHECKOUT_SETTINGS,
  ...DEFAULT_MERCHANT_CART_BUTTON_SETTINGS
};

// TODO: the following fields are newly added in settings, we set these default values here to keep the behavior as phase 2 when settings is not ramped,
// we can delete the following map when settings feature is 100% ramped
const SETTINGS_NEWLY_ADDED_FIELDS_DEFAULT_VALUES = {
  payment_method_paylater: true,
  payment_method_venmo: true,
  payment_method_apple_pay: true,
  show_business_name: false,
  show_business_logo: false,
  homepage_url: '',
  auto_return_url: ''
};

const getSettingsValuesWithFallback = (appContext: IAppContext | null) =>
  appContext?.merchantSettings || {};

const mergeInButtonConfigWithMerchantSettings = (
  merchantSettings: MerchantSettings | undefined,
  buttonConfig: ButtonConfig,
  buttonVariation: ButtonVariation = ButtonVariation.BuyNow
): ButtonConfig => {
  const isCartButton = buttonVariation === ButtonVariation.Cart;
  const calculatedButtonConfig = Object.entries(
    isCartButton ? DEFAULT_CART_FIELDS_MAPPER : DEFAULT_FIELDS_MAPPER
  ).reduce((acc, [settingsKey, mapper]) => {
    const mappingValue =
      merchantSettings?.[settingsKey] ??
      buttonConfig?.[mapper.configMapper] ??
      (isCartButton ? DEFAULT_CART_BUTTON_SETTINGS : DEFAULT_MERCHANT_SETTINGS)[settingsKey];
    const mapping = { [mapper.configMapper]: mappingValue };
    return { ...acc, ...mapping };
  }, {});

  return { ...buttonConfig, ...calculatedButtonConfig };
};

const getButtonVariablesFromMerchantSettings = (
  merchantSettings: MerchantSettings | undefined,
  buttonVariables: Record<string, string>,
  buttonVariation: ButtonVariation = ButtonVariation.BuyNow
): Record<string, string> => {
  const isCartButton = buttonVariation === ButtonVariation.Cart;
  const calculatedButtonVariables = Object.entries(
    isCartButton ? DEFAULT_CART_FIELDS_MAPPER : DEFAULT_FIELDS_MAPPER
  ).reduce((acc, [settingsKey, mapper]) => {
    let mappingValue =
      merchantSettings?.[settingsKey] ??
      buttonVariables?.[mapper.variablesMapper] ??
      (isCartButton ? DEFAULT_CART_BUTTON_SETTINGS : DEFAULT_MERCHANT_SETTINGS)[settingsKey];

    if (mapper.variableHandler) {
      mappingValue = mapper.variableHandler(mappingValue);
    }

    const mapping = { [mapper.variablesMapper]: mappingValue };
    return { ...acc, ...mapping };
  }, {});

  return { ...buttonVariables, ...calculatedButtonVariables };
};

const getAllowedPaymentMethodsFromSettings = (
  paymentMethodVenmo: boolean,
  paymentMethodPayLater: boolean,
  paymentMethodApplePay: boolean,
  allowApplePayToggle: boolean
): CheckoutPaymentOptionsOtherThanPayPal[] => {
  const switches = {
    [CheckoutPaymentOptionsOtherThanPayPal.Venmo]: paymentMethodVenmo,
    [CheckoutPaymentOptionsOtherThanPayPal.PayLater]: paymentMethodPayLater,
    [CheckoutPaymentOptionsOtherThanPayPal.ApplePay]: paymentMethodApplePay && allowApplePayToggle,
    [CheckoutPaymentOptionsOtherThanPayPal.Card]: true
  };
  const allowedMethods = Object.values(CheckoutPaymentOptionsOtherThanPayPal).reduce(
    (acc, paymentOption) => {
      if (switches[paymentOption]) {
        return acc.concat(paymentOption);
      }
      return acc;
    },
    [] as CheckoutPaymentOptionsOtherThanPayPal[]
  );
  return allowedMethods;
};

export {
  SETTINGS_FIELDS_CHECKOUT,
  SETTINGS_FIELDS_STACKED_BUTTON,
  SETTINGS_FIELDS_SINGLE_BUTTON,
  SETTINGS_FIELDS_CART_BUTTON,
  SETTINGS_NEWLY_ADDED_FIELDS_DEFAULT_VALUES,
  SETTINGS_FIELDS_SUBSCRIPTIONS,
  mergeInButtonConfigWithMerchantSettings,
  getButtonVariablesFromMerchantSettings,
  getAllowedPaymentMethodsFromSettings,
  getSettingsValuesWithFallback
};
